// English strings (source language): Settings screen.

const en = {
  title: 'Settings',
  intro: 'Your account, devices, controls and game options.',
  tabs: { account: 'Account', devices: 'Devices', controls: 'Controls', game: 'Game', general: 'General' },
  profile: 'Profile',
  name: 'Name',
  mass: 'Weight (kg)',
  country: 'Country',
  countryNone: 'No flag',
  countryHelp: 'Your flag rides next to your name in races, for every player to see.',
  countrySaved: 'Country saved',
  ftp: 'FTP',
  ftpHelp: 'Gruvetto works out your FTP from your rides: a hard effort raises it at once, and it eases off slowly when you stop pushing. It sets workout targets, zones and bot strength. For an exact figure, do the FTP test or the Ramp test in Training.',
  ftpBasis: {
    seed: 'Provisional: estimated from your weight until you have done a few rides of 10 minutes or more.',
    efforts: 'Estimated from your best efforts.',
    test: 'Measured by your last test, and the efforts since.',
  },
  profileSaved: 'Profile saved',
  trainer: 'Smart trainer',
  trainerHelp: 'FTMS: slope, ERG or resistance level',
  power: 'Power meter',
  powerHelp: 'pedals, crank or hub: its reading takes priority',
  csc: 'Speed / cadence',
  cscHelp: 'wheel or crank sensor',
  hr: 'Heart rate',
  hrHelp: 'chest strap or watch broadcasting over Bluetooth',
  connect: 'Connect',
  reconnect: 'Reconnect',
  disconnect: 'Disconnect',
  classic: 'Classic trainer',
  classicHelp:
    'No electronics (fluid, magnetic, wind): power is estimated from rear wheel speed, measured by a speed sensor. Inflate the tyre and tighten the roller the same way every time. The estimate is approximate (±10%) and the activity is marked “estimated power”.',
  curve: 'Model',
  curveNone: 'None (smart trainer)',
  wheel: 'Wheel circumference',
  wheelMm: (mm: number) => `${mm} mm`,
  wheelCustom: 'Other (mm)',
  classicLive: (kmh: string, w: number) => `Wheel: ${kmh} km/h · estimated power ${w} W`,
  classicNoSensor: 'Connect a speed sensor on the rear wheel.',
  classicIgnored: 'A smart trainer or power meter is active: its reading is used instead.',
  riding: 'Riding',
  graphicsSound: 'Graphics and sound',
  graphics: 'Graphics',
  perfHud: 'Performance overlay',
  perfHudHelp: 'Shows the frame rate at the top of the ride, to tune the graphics level.',
  photos: 'Card photos',
  photoModes: { auto: 'Automatic', on: 'On', off: 'Off' },
  photosHelp:
    'Automatic skips the photos when this browser draws without a graphics card.',
  mood: 'Mood',
  levelReq: (n: number) => `lvl ${n}`,
  trainerDifficulty: (pct: number) => `Trainer difficulty in races: ${pct}%`,
  volume: (pct: number) => `Master volume: ${pct}%`,
  sfxVolume: (pct: number) => `Effects: ${pct}%`,
  ambience: (pct: number) => `Riding sounds: ${pct}%`,
  musicVolume: (pct: number) => `Music: ${pct}%`,
  soundHelp:
    'Effects are the race sounds: start, items, hits, laps. Riding sounds are the wind, tyres, chain, the bunch around you and the roadside crowd. The music follows the game: calm on the menus, driving in a race and rising on the last lap, steady during a workout. Each slider plays a sample when you let go.',
  trackMood: 'Ride each track in its own light',
  ergDefault: 'ERG mode by default during workouts',
  draftAssist: 'Draft assist in races',
  cameraView: 'Default camera view',
  cameraViewHelp: 'The view each ride opens with. Change it during a ride with the Change view control.',
  cameraViews: { chase: 'Chase', close: 'Close behind', high: 'High behind', onboard: 'Onboard', side: 'Side' },
  draftAssistHelp: 'Puts you on the best wheel ahead. Steering by hand pauses it for a few seconds. On a wheel, you are held there: push about seventy watts more than sitting in takes, or steer out, and you come round.',
  culture: 'Language and time',
  appearance: 'Appearance',
  appearanceHelp: 'Colours of the menus on this device.',
  uiThemes: { system: 'System', dark: 'Dark', light: 'Light', ocean: 'Ocean', sunset: 'Sunset' },
  language: 'Language',
  langAuto: (name: string) => `Automatic (${name})`,
  timeZone: 'Time zone',
  tzAuto: (tz: string) => `Follow this device (${tz})`,
  clock: 'Time format',
  clockAuto: (example: string) => `Language default (${example})`,
  clock24: '24-hour',
  clock12: '12-hour (AM/PM)',
  preview: 'Preview:',
  cultureHelp:
    'Streaks, the daily bonus and the weekly leaderboard count days in this time zone.',
  controls: 'Controls',
  controlsHelp: 'Keyboard, gamepad, Zwift controllers and touch screen can be used together.',
  touch: 'On-screen controls',
  touchModes: { auto: 'Automatic (touch screens)', on: 'Always shown', off: 'Never' },
  touchHelp: 'Tap the left or right side of the screen to move across the road. The other buttons sit under your thumbs.',
  keyboardTitle: 'Keyboard',
  keysHelp: 'Click “Assign” then press a key. Esc always opens the menu.',
  keyReserved: (key: string) => `${key} is reserved and cannot be assigned.`,
  clearKeys: 'Remove the keys of this action',
  otherKeys: 'Other keys:',
  gamepad: 'Gamepad',
  gamepadNone: 'No gamepad detected. Plug one in or pair it, then press any of its buttons.',
  gamepadFound: (names: string) => `Detected: ${names}`,
  gamepadMap: {
    steerLeft: 'Left stick, D-pad ←',
    steerRight: 'Left stick, D-pad →',
    fire: 'A / ✕, right trigger',
    gearUp: 'Right bumper, D-pad ↑',
    gearDown: 'Left bumper, D-pad ↓',
    look: 'B / ◯',
    camera: 'Y / △',
    pause: 'Start / Options',
  },
  buttons: 'Controller buttons',
  defaults: 'Defaults',
  mapHelp: 'Click “Assign” then press the controller button.',
  lastButton: 'Last button received:',
  holding: 'Pressed:',
  testerIdle: 'Press a button on a controller to test it.',
  assign: 'Assign',
  press: 'Press…',
  rawMessages: 'Raw controller messages',
  keys: {
    watts: 'simulated watts',
    shift: 'Shift',
    esc: 'Esc',
    workout: 'workout',
    intensity: 'intensity',
    next: 'next block',
    space: 'Space',
  },
};
export default en;
